(ns frontend.view.settings2.surveys.events
    (:require
     [ajax.core :as ajax]
     [common.util :as cu]
     [frontend.controller.websocket.event :as ws]
     [frontend.db :as app-db]
     [frontend.logging :as logging]
     [frontend.state.current-session :as session]
     [frontend.utils :as fu]
     [frontend.view.modals.entity-editor.view :as entity-editor]
     [frontend.view.modals.events :as modals]
     [frontend.view.settings2.surveys.question.sub :as question-sub]
     [frontend.view.settings2.surveys.sub :as sub]
     [potpuri.core :as potpuri]
     [re-frame.core :as rf]
     [ui.util :as uu]))

(rf/reg-event-fx
  ::clean-up
  (fn [{db :db} _]
    {:db (-> db
             (potpuri/dissoc-in [::sub/tenant-questions])
             (potpuri/dissoc-in [::sub/block-order-draft]))}))

(rf/reg-event-fx
  ::on-data-loaded-ok
  (fn [{db :db} [_ resp]]
    (let [body (:body resp)
          questions (->> body
                         (cu/filter-by :entity/type :entity.type/survey-question))
          questions-ext-ids (->> body
                                 (cu/filter-by :entity/type :entity.type/survey-question-ext-id))]
      {:db (-> db
               (assoc-in [::sub/tenant-questions] questions)
               (assoc-in [::sub/tenant-questions-ext-ids] questions-ext-ids))})))

(rf/reg-event-fx
  ::on-data-load-failed
  (fn [{_db :db} [_ resp]]
    (logging/unimplemented "Failed to load survey data : " resp)))

(rf/reg-event-fx
  ::load-data
  (fn [{_db :db} _]
    {:http-xhrio {:method          :post
                  :uri             "/surveys/api/load-questions"
                  :params          {}
                  :timeout         30000
                  :format          (ajax/transit-request-format)
                  :response-format (ajax/transit-response-format)
                  :on-success      [::on-data-loaded-ok]
                  :on-failure      [::on-data-load-failed]}}))

(rf/reg-event-fx
  ::start-create-question
  (fn [{db :db} _]
    {:db (-> db
             (assoc-in [::sub/question-create-open?] true)
             (assoc-in [::question-sub/numeric-questions]
                       (->> (get-in db [::sub/tenant-questions])
                            (filter (fn [q]
                                      (some #{(:survey-question/data-type q)}
                                            [:data.type/number
                                             :data.type/currency-value
                                             :data.type/percentage
                                             :data.type/integer
                                             :data.type/long
                                             :data.type/multiplier]))))))}))

(rf/reg-event-fx
  ::start-question-edit
  (fn [{db :db} [_ survey-question]]
    (let [question-languages (:languages survey-question)
          question-ext-ids (->> (:survey-question/external-ids survey-question)
                                (mapv (fn [ext-id-ref]
                                        (->> (get-in db [::sub/tenant-questions-ext-ids])
                                             (filter #(= (:db/id %) (:db/id ext-id-ref)))
                                             (first)))))
          question-data (cond-> (-> (select-keys
                                      survey-question
                                      [:db/id
                                       :survey-question/label-translations
                                       :survey-question/info-translations
                                       :survey-question/keywords
                                       :survey-question/data-type
                                       :survey-question/data-unit
                                       :survey-question/options
                                       :survey-question/calculation-rules
                                       :survey-question/multi-file?
                                       :survey-question/accepted-file-types
                                       :survey-question/file-name-abbreviation
                                       :survey-question/unique-identifier?])
                                    (assoc :survey-question/external-ids
                                           (mapv #(some-> % uu/with-react-key) question-ext-ids)))
                          (:survey-question/options survey-question)
                          (update :survey-question/options #(mapv uu/with-react-key %)))]
      {:db (-> db
               (assoc-in [::sub/question-create-open?] true)
               (assoc-in [::question-sub/survey-question-changes] question-data)
               (assoc-in [::question-sub/saved-survey-question] question-data)
               (assoc-in [::question-sub/numeric-questions]
                         (->> (get-in db [::sub/tenant-questions])
                              (filter (fn [q]
                                        (some #{(:survey-question/data-type q)}
                                              [:data.type/number
                                               :data.type/currency-value
                                               :data.type/percentage
                                               :data.type/integer
                                               :data.type/long
                                               :data.type/multiplier])))))
               (assoc-in [::question-sub/selected-language]
                         (or (some #{:ENG} question-languages)
                             (first question-languages))))})))
(rf/reg-event-fx
  ::close-question-creator
  (fn [{db :db} _]
    {:db (-> db
             (assoc-in [::sub/question-create-open?] false)
             (potpuri/dissoc-in [::question-sub/survey-question-changes])
             (potpuri/dissoc-in [::question-sub/saved-survey-question])
             (potpuri/dissoc-in [::question-sub/numeric-questions]))
     :fx [[:dispatch [::load-data]]]}))

(rf/reg-event-fx
  ::start-edit-template
  (fn [{db :db} [_ survey-template-id]]
    (let [survey-template (session/get-entity survey-template-id)]
      {:db (-> db
               (potpuri/dissoc-in [::sub/block-order-draft])
               (assoc-in [::sub/survey-template] survey-template)
               (assoc-in [::sub/config-draft] (:survey-template/config survey-template)))})))

(rf/reg-event-fx
  ::close-template-editor
  (fn [{db :db} [_]]
    {:db (-> db
             (potpuri/dissoc-in [::sub/config-draft])
             (potpuri/dissoc-in [::sub/survey-template]))}))

(defn key-survey-block
  "Attach client-only react-keys to a config block and, for list-content blocks
   (section/table), each of its content items. Wysiwyg blocks whose :content is a
   lang->text map are left untouched."
  [block]
  (cond-> (uu/with-react-key block)
    (sequential? (:content block))
    (update :content #(mapv uu/with-react-key %))))

(rf/reg-event-fx
  ::start-edit-survey-block
  (fn [{db :db} [_ survey-block-id]]
    (let [survey-block (session/get-entity survey-block-id)
          config (:survey-block/config survey-block)]
      {:db (assoc db ::sub/survey-block survey-block
                  ::sub/config-draft (update config :blocks #(mapv key-survey-block %)))})))

(rf/reg-event-fx
  ::close-survey-block-editor
  (fn [{db :db} [_]]
    {:db (dissoc db
                 ::sub/config-draft
                 ::sub/survey-block)}))

(rf/reg-event-fx
  ::on-save-template-ok
  (fn [{db :db} [_ changes block-order]]
    (js/console.log "Update saved block order : " block-order)
    (let [db-with-block-order (if block-order
                                (assoc-in db [::sub/survey-template :survey-template/block-order] block-order)
                                db)]
      {:db (-> db-with-block-order
               (assoc-in [::sub/survey-template :survey-template/config] changes))})))

(rf/reg-event-fx
  ::on-save-template-failed
  (fn [{_db :db} [_]]
    (logging/unimplemented "Unimplmeented on-save-draft-failed")
    {}))

(rf/reg-event-fx
  ::save-template
  (fn [{db :db} [_]]
    (let [template (get-in db [::sub/survey-template])
          config-draft (get-in db [::sub/config-draft])
          block-order (get-in db [::sub/block-order-draft])]
      (when (or (not (cu/blank-value? config-draft))
                block-order)
        {:http-xhrio {:method          :post
                      :uri             "/surveys/api/save-template"
                      :params          {:db/id           (:db/id template)
                                        :template-config config-draft
                                        :block-order     (or block-order
                                                             (:survey-template/block-order template)
                                                             (vec (:survey-template/template-blocks template)))}
                      :timeout         30000
                      :format          (ajax/transit-request-format)
                      :response-format (ajax/transit-response-format)
                      :on-success      [::on-save-template-ok config-draft block-order]
                      :on-failure      [::on-save-template-failed]}}))))

(rf/reg-event-fx
  ::on-save-survey-block-ok
  (fn [{db :db} [_ changes]]
    {:db (assoc-in db [::sub/survey-block :survey-block/config] changes)}))

(rf/reg-event-fx
  ::on-save-survey-block-failed
  (fn [{_db :db} [_]]
    (logging/unimplemented "Unimplemented on-save-survey-block-failed")
    {}))

(rf/reg-event-fx
  ::save-survey-block
  (fn [{db :db} [_]]
    (let [template (get-in db [::sub/survey-block])
          config-draft (get-in db [::sub/config-draft])]
      (when-not (cu/blank-value? config-draft)
        {:http-xhrio {:method          :post
                      :uri             "/surveys/api/save-block"
                      :params          {:db/id        (:db/id template)
                                        :block-config config-draft}
                      :timeout         30000
                      :format          (ajax/transit-request-format)
                      :response-format (ajax/transit-response-format)
                      :on-success      [::on-save-survey-block-ok config-draft]
                      :on-failure      [::on-save-survey-block-failed]}}))))

(rf/reg-event-fx
  ::on-save-emails-ok
  (fn [{_db :db} [_ close-modal!]]
    (close-modal!)
    {}))

(rf/reg-event-fx
  ::on-save-emails-failed
  (fn [{_db :db} [_]]
    (logging/unimplemented "Unimplmeented on-save-draft-failed")
    {}))
(rf/reg-event-fx
  ::save-email-localizations
  (fn [{_db :db} [_ data close-modal!]]
    {:http-xhrio {:method          :post
                  :uri             "/surveys/api/save-template-emails"
                  :params          (->> data
                                        (map (fn [[k v]]
                                               (if (= k :db/id)
                                                 [k v]
                                                 [k (pr-str v)])))
                                        (into {}))
                  :timeout         30000
                  :format          (ajax/transit-request-format)
                  :response-format (ajax/transit-response-format)
                  :on-success      [::on-save-emails-ok close-modal!]
                  :on-failure      [::on-save-emails-failed close-modal!]}}))

(rf/reg-event-fx
  ::save-thank-you-localizations
  (fn [{_db :db} [_ data close-modal!]]
    {:http-xhrio {:method          :post
                  :uri             "/surveys/api/save-template-thank-you"
                  :params          (->> data
                                        (map (fn [[k v]]
                                               (if (= k :db/id)
                                                 [k v]
                                                 [k (pr-str v)])))
                                        (into {}))
                  :timeout         30000
                  :format          (ajax/transit-request-format)
                  :response-format (ajax/transit-response-format)
                  :on-success      [::on-save-emails-ok close-modal!]
                  :on-failure      [::on-save-emails-failed close-modal!]}}))

(rf/reg-event-fx
  ::select-lang
  (fn [{db :db} [_ lang]]
    {:db (-> db
             (assoc-in [::sub/selected-language] lang)
             (assoc-in [::sub/lang-select-open?] false))}))

(rf/reg-event-fx
  ::select-reference-period
  (fn [{db :db} [_ period]]
    {:db (-> db
             (assoc-in [::sub/config-draft :reference-period] period))}))

(rf/reg-event-fx
  ::set-mandatoriness-style
  (fn [{db :db} [_ style]]
    {:db (assoc-in db [::sub/config-draft :mandatoriness-style] style)}))

(rf/reg-event-fx
  ::set-show-questions-ext-ids?
  (fn [{db :db} [_ v]]
    {:db (assoc-in db [::sub/config-draft :show-questions-ext-ids?] v)}))

(rf/reg-event-fx
  ::set-multi-survey?
  (fn [{db :db} [_ v]]
    {:db (assoc-in db [::sub/config-draft :multi-survey?] v)}))

(rf/reg-event-fx
  ::set-hide-optional?
  (fn [{db :db} [_ v]]
    {:db (assoc-in db [::sub/config-draft :hide-no-answer?] v)}))

(rf/reg-event-fx
  ::set-excel-info-text
  (fn [{db :db} [_ lang v]]
    (js/console.log "Set info text : " lang " > " v)
    {:db (assoc-in db [::sub/config-draft :excel-info-text lang] v)}))

(rf/reg-event-fx
  ::set-show-excel-import?
  (fn [{db :db} [_ v]]
    {:db (assoc-in db [::sub/config-draft :excel-info-show?] v)}))

(rf/reg-event-fx
  ::set-block-info
  (fn [{db :db} [_ {:keys [idx lang]} value]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks]
                       (->> (get-in db [::sub/config-draft :blocks])
                            (map-indexed (fn [ix block]
                                           (if (not= ix idx)
                                             block
                                             (assoc-in block [:info lang] value))))
                            (vec))))}))

(rf/reg-event-fx
  ::set-survey-item-value
  (fn [{db :db} [_ {:keys [idx _lang]} {:keys [item-index]} value]]
    {:db (-> db
             (assoc-in [::sub/config-draft
                        :blocks idx
                        :content item-index
                        :value]
                       value))}))

(rf/reg-event-fx
  ::set
  (fn [{db :db} [_ path value]]
    (let [old-value (get-in db path)
          _ (js/console.log "SET : " path " " old-value)
          new-db (assoc-in db path value)]
      {:db new-db})))

(rf/reg-event-fx
  ::remove-vec-item
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))]
      {:db (update-in db
                      vec-path
                      (fn [orig-vec]
                        (cu/vec-remove index orig-vec)))})))

(rf/reg-event-fx
  ::move-block-first
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))]
      {:db (update-in db
                      vec-path
                      (fn [orig-vec]
                        (cu/vec-move orig-vec index 0)))})))

(rf/reg-event-fx
  ::move-block-up
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))]
      (when (pos? index)
        {:db (update-in db
                        vec-path
                        (fn [orig-vec]
                          (cu/vec-move orig-vec index (dec index))))}))))

(rf/reg-event-fx
  ::move-block-down
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))]
      (when (< index (dec (count (get-in db vec-path))))
        {:db (update-in db
                        vec-path
                        (fn [orig-vec]
                          (cu/vec-move orig-vec index (inc index))))}))))

(rf/reg-event-fx
  ::set-question-search-data-types
  (fn [{db :db} [_ v]]
    {:db (-> db
             (assoc-in [::sub/question-search-data-types] v))}))

(rf/reg-event-fx
  ::set-question-search-str
  (fn [{db :db} [_ v]]
    {:db (-> db
             (assoc-in [::sub/question-search-str] v))}))

(rf/reg-event-fx
  ::toggle-question-selection
  (fn [{db :db} [_ q selected?]]
    (let [existing-selections (set (get-in db [::sub/tenant-question-selections]))
          question-id (:db/id q)
          updated-selection (if selected?
                              (conj existing-selections question-id)
                              (disj existing-selections question-id))]
      {:db (-> db
               (assoc-in [::sub/tenant-question-selections] updated-selection))})))

(rf/reg-event-fx
  ::add-block
  (fn [{db :db} [_ block-type]]
    {:db (update-in db [::sub/config-draft :blocks]
                    (fnil conj [])
                    (uu/with-react-key {:block-type block-type}))}))

(rf/reg-event-fx
  ::add-survey-item
  (fn [{db :db} [_ db-path template? item-type]]
    (js/console.log "Add survey item to : " db-path " < " item-type)
    {:db (-> db
             (update-in db-path
                        (fn [block-content]
                          (if (= :survey-item/question item-type)
                            (vec block-content)
                            (conj (vec block-content)
                                  (uu/with-react-key {:survey-item-type item-type
                                                      :visibility       :visibility/always})))))
             (assoc-in [::sub/question-selector-open?] (= :survey-item/question item-type))
             (assoc-in [::sub/question-selector-languages] (if template?
                                                             (get-in db [::sub/survey-template :survey-template/languages])
                                                             (get-in db [::sub/survey-block :survey-block/languages])))
             (assoc-in [::sub/question-selector-path] db-path))}))

(rf/reg-event-fx
  ::set-question-mandatoriness
  (fn [{db :db} [_ idx item-index mandatoriness]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :content item-index :mandatoriness] mandatoriness)
             (assoc-in [::sub/question-mandatoriness-open? idx item-index] false))}))

(rf/reg-event-fx
  ::set-question-visibility
  (fn [{db :db} [_ idx item-index visibility]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :content item-index :visibility] visibility)
             (assoc-in [::sub/question-visibility-open? idx item-index] false))}))

(rf/reg-event-fx
  ::set-table-history
  (fn [{db :db} [_ idx history]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :history] history))}))

(rf/reg-event-fx
  ::set-table-history-in-use?
  (fn [{db :db} [_ idx in-use?]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :history-in-use?] (boolean in-use?)))}))

(rf/reg-event-fx
  ::set-table-history-snapshot?
  (fn [{db :db} [_ idx in-use?]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :snapshot-history?] (boolean in-use?)))}))

(rf/reg-event-fx
  ::set-table-forecast-in-use?
  (fn [{db :db} [_ idx in-use?]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :forecast-in-use?] in-use?))}))

(rf/reg-event-fx
  ::set-table-forecast-snapshot?
  (fn [{db :db} [_ idx in-use?]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :snapshot-forecast?] (boolean in-use?)))}))

(rf/reg-event-fx
  ::add-table-question
  (fn [{db :db} [_ idx question-id]]
    {:db (-> db
             (update-in [::sub/config-draft :blocks idx :content]
                        (fn [coll]
                          (vec (concat coll
                                       [{:survey-question-id question-id
                                         :survey-item-type   :survey-item/question
                                         :visibility         :visibility/always
                                         :mandatoriness      :mandatory/always}])))))}))

(rf/reg-event-fx
  ::remove-selected-questions
  (fn [{db :db} [_ idx question-ids]]
    {:db (-> db
             (update-in [::sub/config-draft :blocks idx :content]
                        (fn [coll]
                          (->> coll
                               (remove (fn [q]
                                         (some #{(:survey-question-id q)} question-ids)))
                               (vec)))))}))
(rf/reg-event-fx
  ::move-table-question
  (fn [{db :db} [_ idx from-index to-index]]
    {:db (-> db
             (update-in [::sub/config-draft :blocks idx :content]
                        (fn [coll]
                          (cu/vec-move coll from-index to-index))))}))

(rf/reg-event-fx
  ::set-table-forecast
  (fn [{db :db} [_ idx forecast]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks idx :forecast] forecast))}))

(rf/reg-event-fx
  ::move-item-first
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))
          orig-vec (vec (get-in db vec-path))
          modified-vec (cu/vec-move orig-vec index 0)]
      {:db (assoc-in db vec-path modified-vec)})))

(rf/reg-event-fx
  ::move-item-down
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))
          orig-vec (vec (get-in db vec-path))
          modified-vec (cu/vec-move orig-vec index (inc index))]
      {:db (assoc-in db vec-path modified-vec)})))

(rf/reg-event-fx
  ::move-item-up
  (fn [{db :db} [_ item-path]]
    (let [index (last item-path)
          vec-path (vec (drop-last item-path))
          orig-vec (vec (get-in db vec-path))
          modified-vec (cu/vec-move orig-vec index (dec index))]
      {:db (assoc-in db vec-path modified-vec)})))

(rf/reg-event-fx
  ::remove-item
  (fn [{db :db} [_ {:keys [idx]} {:keys [item-index]}]]
    (let [block-content (vec (get-in db [::sub/config-draft :blocks idx :content]))
          new-block-content (cu/vec-remove item-index block-content)]
      {:db (-> db
               (assoc-in [::sub/config-draft :blocks idx :content] new-block-content))})))

(rf/reg-event-fx
  ::close-question-selector
  (fn [{db :db} [_]]
    {:db (dissoc db
                 ::sub/question-selector-open?
                 ::sub/question-selector-languages
                 ::sub/question-selector-path
                 ::sub/tenant-question-selections
                 ::sub/question-search-str
                 ::sub/question-search-data-types)}))

(rf/reg-event-fx
  ::select-question
  (fn [{db :db} [_ question]]
    {:db (-> db
             (update-in [::sub/selected-questions]
                        (fn [qs]
                          (vec (concat qs [question])))))}))

(rf/reg-event-fx
  ::remove-question
  (fn [{db :db} [_ question-id]]
    {:db (-> db
             (update-in [::sub/selected-questions]
                        (fn [qs]
                          (->> qs
                               (remove (fn [{:keys [db/id]}]
                                         (= id question-id)))
                               (vec)))))}))

(rf/reg-event-fx
  ::add-selected-questions
  (fn [{db :db} [_ _question-id]]
    (let [selector-path (get db ::sub/question-selector-path)
          selected-questions (->> (get db ::sub/selected-questions)
                                  (mapv (fn [{:keys [db/id]}]
                                          (uu/with-react-key {:survey-question-id id
                                                              :survey-item-type   :survey-item/question
                                                              :visibility         :visibility/always
                                                              :mandatoriness      :mandatory/always}))))]
      {:db (-> db
               (assoc-in selector-path (into (vec (get-in db selector-path))
                                             selected-questions))
               (dissoc ::sub/question-selector-open?)
               (dissoc ::sub/selected-questions))
       :fx [[:dispatch [::close-question-selector]]]})))

(rf/reg-event-fx
  ::add-question
  (fn [{db :db} [_ question-id]]
    (let [selector-path (get db ::sub/question-selector-path)
          all-questions (conj (vec (get-in db selector-path))
                              (uu/with-react-key {:survey-question-id question-id
                                                  :survey-item-type   :survey-item/question
                                                  :visibility         :visibility/always
                                                  :mandatoriness      :mandatory/always}))]
      {:db (-> db
               (assoc-in selector-path all-questions)
               (dissoc ::sub/question-selector-open?))
       :fx [[:dispatch [::close-question-selector]]]})))

(rf/reg-event-fx
  ::cancel-select
  (fn [{db :db} [_]]
    {:db (-> db
             (dissoc ::sub/question-selector-open?)
             (dissoc ::sub/selected-questions))}))

(rf/reg-event-fx
  ::remove-block
  (fn [{db :db} [_ idx]]
    {:db (-> db
             (assoc-in [::sub/config-draft :blocks]
                       (cu/vec-remove idx (get-in db [::sub/config-draft :blocks]))))}))

(rf/reg-event-fx
  ::change-tab
  (fn [{db :db} [_ tab]]
    {:db (-> db
             (assoc-in [::sub/selected-tab] tab))}))

(rf/reg-event-fx
  ::reorder-survey-blocks
  (fn [{db :db} [_ from-index to-index]]
    (let [block-order (or (get-in db [::sub/block-order-draft])
                          (get-in db [::sub/survey-template :survey-template/block-order])
                          (vec (get-in db [::sub/survey-template :survey-template/template-blocks])))]
      (js/console.log "Reorder : " block-order " > " from-index " -> " to-index " <<< " (get-in db [::sub/survey-template :survey-template/block-order]))
      {:db (-> db
               (assoc-in [::sub/block-order-draft] (cu/vec-move (vec block-order) from-index to-index)))})))

(rf/reg-event-fx
  ::remove-survey-block
  (fn [{db :db} [_ id]]
    (let [block-order (or (get-in db [::sub/block-order-draft])
                          (get-in db [::sub/survey-template :survey-template/block-order])
                          (vec (get-in db [::sub/survey-template :survey-template/template-blocks])))
          new-block-order (->> block-order
                               (remove (fn [block-id]
                                         (= id block-id)))
                               (vec))]
      (js/console.log "Remove survey block : " block-order " > " new-block-order)
      {:db (-> db
               (assoc-in [::sub/block-order-draft] new-block-order))})))

(rf/reg-event-fx
  ::add-block-to-template
  (fn [{db :db} [_ new-block-id]]
    (let [block-order (or (get-in db [::sub/block-order-draft])
                          (get-in db [::sub/survey-template :survey-template/block-order])
                          (vec (get-in db [::sub/survey-template :survey-template/template-blocks])))]
      (js/console.log "Add survey block : " block-order " > " new-block-id)
      {:db (-> db
               (assoc-in [::sub/block-order-draft] (vec (conj block-order new-block-id))))})))

(rf/reg-event-fx
  ::clone-error
  (fn [{_db :db} [_ block-ids resp]]
    (js/console.log "Failed to clone " block-ids " > " resp)))

(rf/reg-event-fx
  ::clone-success
  (fn [{_db :db} [_ entity-ids resp]]
    (let [new-entities (:body resp)]
      (fu/manage-entities session/reverse-lookup
                          app-db/session
                          new-entities)
      (when (= 1 (count entity-ids))
        (modals/open-modal! entity-editor/modal-key
                            {:entity-id (:db/id (first new-entities))})))))

(rf/reg-event-fx
  ::clone-blocks
  (fn [{db :db} [_ block-ids]]
    {:db       (assoc-in db [::sub/processing-blocks-selection] true)
     ::ws/send {:id         :entity/clone-entities
                :body       {:entity-ids (vec block-ids)}
                :on-success [::clone-success block-ids]
                :on-error   [::clone-error block-ids]}}))

(rf/reg-event-fx
  ::clone-templates
  (fn [{db :db} [_ template-ids]]
    {:db       (assoc-in db [::sub/processing-blocks-selection] true)
     ::ws/send {:id         :entity/clone-entities
                :body       {:entity-ids (vec template-ids)}
                :on-success [::clone-success template-ids]
                :on-error   [::clone-error template-ids]}}))

(rf/reg-event-fx
 ::rotation-period-status-updated
 (fn [_ _]
   {}))

(rf/reg-event-fx
 ::rotation-period-status-update-failed
 (fn [_ [_ rotation-period-id status resp]]
   (js/console.warn "Failed to set status " status " on " rotation-period-id " > " resp)))

(rf/reg-event-fx
 ::set-rotation-period-status
 (fn [_ [_ rotation-period-id status]]
   {::ws/send {:id         :entity/update-attribute
               :body       {:db/id             rotation-period-id
                            :attribute         :rotation-period/rotation-period-status
                            :value             status
                            :instant-indexing? true}
               :on-success [::rotation-period-status-updated]
               :on-error   [::rotation-period-status-update-failed rotation-period-id status]}}))
